import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import EstimateWidget from "@/components/EstimateWidget";
import { CUSTOMER_TYPES, JOB_TYPES, TIMEFRAMES, YES_NO_NOTSURE, STATES } from "@/lib/constants";
import { BUSINESS } from "@/lib/constants";
import { api, formatError } from "@/lib/api";
import { Upload, X, Loader2, Calculator } from "lucide-react";

export default function RequestQuote() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    site_address: "", suburb: "", state: "VIC", postcode: "",
    customer_type: "Homeowner", job_type: "Vacant lot & pre-build clean-up",
    site_size: "", has_construction_waste: "Not sure", has_green_waste: "Not sure",
    has_soil_rubble: "Not sure", has_hazardous: "Not sure",
    timeframe: "Flexible", message: "", consent: false,
    service_category: "", size: "", estimate_min: null, estimate_max: null,
    service_kind: "cleanup", skip_bin_size: "", skip_bin_waste: "general",
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        // Stable per-photo id so React can key correctly across re-renders
        const pid = (window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
        setPhotos((p) => [...p, { id: pid, url: data.url, name: f.name }]);
      }
      toast.success("Photos uploaded");
    } catch (err) {
      toast.error(formatError(err) || "Photo upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (id) => setPhotos(photos.filter((p) => p.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please tick the consent box to submit.");
    setSubmitting(true);
    try {
      const payload = { ...form, photo_urls: photos.map((p) => p.url) };
      const { data } = await api.post("/leads", payload);
      nav(`/thank-you?ref=${encodeURIComponent(data.reference || data.id)}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const field = (label, key, type = "text", required = false, placeholder = "") => (
    <label className="block">
      <span className="rs-label">{label}{required && <span className="text-safety"> *</span>}</span>
      <input data-testid={`quote-${key}`} className="rs-input" type={type} required={required} placeholder={placeholder} value={form[key]} onChange={set(key)} />
    </label>
  );

  const select = (label, key, options, required = false) => (
    <label className="block">
      <span className="rs-label">{label}{required && <span className="text-safety"> *</span>}</span>
      <select data-testid={`quote-${key}`} className="rs-input" required={required} value={form[key]} onChange={set(key)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );

  return (
    <PublicLayout>
      <Seo title="Request a Site Clean-Up Quote | Site Cleanup" description="Request a quote for vacant lot clean-up, builder waste removal, pre-build clean-up or end-of-build tidy-up. Upload photos and site details." />
      <section className="bg-asphalt text-white py-16 md:py-24">
        <div className="rs-container">
          <span className="rs-eyebrow text-safety">Free quote · No obligation</span>
          <h1 className="mt-3 text-4xl md:text-6xl uppercase max-w-3xl">Get your free site clean-up quote.</h1>
          <p className="mt-4 max-w-2xl text-white/75">Fill in the short form below. We'll review your enquiry, match it with one suitable local contractor, and come back to you with a written quote within 1 business day.</p>
          <div className="mt-6 flex flex-wrap gap-2 text-[11px] uppercase tracking-widest">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5"><span className="w-1.5 h-1.5 bg-safety rounded-full" /> Takes 2–3 minutes</span>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5"><span className="w-1.5 h-1.5 bg-safety rounded-full" /> Reply within 1 business day</span>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5"><span className="w-1.5 h-1.5 bg-safety rounded-full" /> One contractor, not a bidding pool</span>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <aside className="md:col-span-1 space-y-6 text-sm">
            <div className="bg-white border border-border p-5">
              <p className="rs-eyebrow">What happens next</p>
              <ol className="mt-3 space-y-3 text-muted-foreground list-none">
                <li className="flex gap-3"><span className="font-display text-safety text-lg leading-none">1.</span><span>You submit the form (2–3 min).</span></li>
                <li className="flex gap-3"><span className="font-display text-safety text-lg leading-none">2.</span><span>We review the photos and match your job with one suitable local contractor.</span></li>
                <li className="flex gap-3"><span className="font-display text-safety text-lg leading-none">3.</span><span>You get a written quote by phone or email within <strong className="text-asphalt">1 business day</strong>.</span></li>
                <li className="flex gap-3"><span className="font-display text-safety text-lg leading-none">4.</span><span>You decide. Accept, ask questions, or walk away — no charge either way.</span></li>
              </ol>
            </div>
            <div className="border-l-4 border-safety pl-4">
              <p className="rs-eyebrow">Photos = accurate quote</p>
              <p className="mt-2 text-muted-foreground">A wide shot plus close-ups of the waste give the fastest, most accurate quote. No photos? We'll still respond, but pricing may be a range until we can see the site.</p>
            </div>
            <div className="border-l-4 border-asphalt pl-4">
              <p className="rs-eyebrow">Hazardous materials</p>
              <p className="mt-2 text-muted-foreground">We don't handle asbestos, contaminated soil, chemicals or hazardous waste directly. If you suspect any of these are on site, please disclose it — we can refer suitable specialists where available.</p>
            </div>
            <div className="border-l-4 border-safety pl-4">
              <p className="rs-eyebrow">Urgent?</p>
              <p className="mt-2 text-muted-foreground">Select "ASAP" on the timeframe and note "urgent" in your message — priority enquiries are actioned faster, subject to local contractor availability.</p>
            </div>
          </aside>

          <form onSubmit={submit} className="md:col-span-2 bg-white border border-border p-6 md:p-8 space-y-6" data-testid="quote-form">
            <div>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="font-display text-2xl text-safety">01</span>
                <h3 className="font-display uppercase text-lg">Your details</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field("Full name", "full_name", "text", true, "e.g. Sam Nguyen")}
                {field("Phone number", "phone", "tel", true, "e.g. 0400 123 456")}
                {field("Email address", "email", "email", true, "you@email.com")}
                {select("I am a…", "customer_type", CUSTOMER_TYPES, true)}
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="font-display text-2xl text-safety">02</span>
                <h3 className="font-display uppercase text-lg">Site details</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field("Site address", "site_address", "text", true, "e.g. 12 Example St")}
                {field("Suburb", "suburb", "text", true, "e.g. Epping")}
                {select("State", "state", STATES, true)}
                {field("Postcode", "postcode", "text", true, "e.g. 3076")}
                {select("What kind of clean-up?", "job_type", JOB_TYPES, true)}
                {field("Approximate site size (optional)", "site_size", "text", false, "e.g. 400sqm, ute load, 2 trailers")}
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-display text-2xl text-safety">03</span>
                <h3 className="font-display uppercase text-lg flex items-center gap-2"><Calculator size={16} className="text-safety" /> Instant estimate <span className="text-[10px] font-normal text-muted-foreground normal-case tracking-normal ml-1">optional</span></h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Pick the closest service and enter a rough size — we'll show an indicative Melbourne market range while you fill in the rest.</p>
              <EstimateWidget
                category={form.service_category}
                size={form.size}
                service_kind={form.service_kind}
                skip_bin_size={form.skip_bin_size}
                skip_bin_waste={form.skip_bin_waste}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              />
            </div>

            <div className="border-t border-border pt-6">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-display text-2xl text-safety">04</span>
                <h3 className="font-display uppercase text-lg">What's on the site?</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">This helps us send your enquiry to the right contractor. Not sure? Just pick "Not sure".</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {select("Construction waste (timber, plaster, brick)?", "has_construction_waste", YES_NO_NOTSURE, true)}
                {select("Green waste (grass, branches, vegetation)?", "has_green_waste", YES_NO_NOTSURE, true)}
                {select("Soil, rock or rubble?", "has_soil_rubble", YES_NO_NOTSURE, true)}
                {select("Tyres, mattresses, chemicals, asbestos or other hazardous?", "has_hazardous", YES_NO_NOTSURE, true)}
                {select("How soon do you need this done?", "timeframe", TIMEFRAMES, true)}
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-display text-2xl text-safety">05</span>
                <h3 className="font-display uppercase text-lg">Photos & notes</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Optional but strongly recommended — photos give you a more accurate quote.</p>
              <label htmlFor="quote-photos" className="block border-2 border-dashed border-border p-6 text-center cursor-pointer hover:border-safety transition-colors" data-testid="quote-upload-dropzone">
                <Upload className="mx-auto text-safety" size={22} />
                <p className="mt-2 text-sm font-semibold">Click to upload site photos</p>
                <p className="text-xs text-muted-foreground">JPG, PNG or WEBP · up to 10MB each · you can add multiple</p>
                <input id="quote-photos" data-testid="quote-photo-input" type="file" multiple accept="image/*" onChange={handlePhotos} className="hidden" />
              </label>
              {uploading && <p className="text-sm text-safety mt-2 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Uploading…</p>}
              {photos.length > 0 && (
                <div className="mt-4 grid grid-cols-3 md:grid-cols-4 gap-3">
                  {photos.map((p) => (
                    <div key={p.id} className="relative group border border-border">
                      <img src={p.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url} alt={p.name} className="w-full h-24 object-cover" />
                      <button type="button" onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 bg-white p-1 border border-border hover:border-safety" aria-label="Remove photo"><X size={14}/></button>
                    </div>
                  ))}
                </div>
              )}

              <label className="block mt-4">
                <span className="rs-label">Anything else we should know? (optional)</span>
                <textarea data-testid="quote-message" rows={4} className="rs-input" value={form.message} onChange={set("message")} placeholder="e.g. gate code, best time to visit, builder's start date, anything unusual about the site" />
              </label>
            </div>

            <div className="border-t border-border pt-6">
              <label className="flex items-start gap-3 text-sm">
                <input data-testid="quote-consent" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-safety" />
                <span className="text-muted-foreground">I'd like to receive a quote for this job. My details can be shared with <strong className="text-asphalt">one matched local contractor</strong> — not a bidding pool — and won't be sold or used for unrelated marketing. See our <a href="/privacy" className="underline">Privacy Policy</a>.</span>
              </label>

              <button type="submit" disabled={submitting} className="rs-btn-primary w-full mt-6 disabled:opacity-70" data-testid="quote-submit">
                {submitting ? <><Loader2 className="animate-spin mr-2" size={16}/> Sending your enquiry…</> : "Send my free quote request"}
              </button>
              <p className="text-xs text-muted-foreground text-center mt-3">You'll get a confirmation email straight away. We'll reply with a quote within 1 business day.</p>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
